import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useT } from "@transifex/react";
import { useQuestionnaire } from "../../../context/questionnaire";
import {
  ContentCard,
  FixedNavBar,
  NavRow,
  NavButton,
  QuestionTitle,
} from "../../../layouts/questionnaire-layout/styles";
import styled from "styled-components";
import { formatAnswerForDisplay, evaluateConditions } from "../../../helpers/questionnaire-helpers";
import { Answer } from "../../../context/questionnaire/types";

const SummaryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }
`;

const SummaryContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #6b6b76;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 16px;
  color: #4a4d5e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EditLink = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 14px;
  cursor: pointer;
  padding: 0 0 0 12px;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const SummaryPage: React.FC = () => {
  const t = useT();
  const {
    questions,
    answers,
    visiblePages,
    goToPage,
    submitQuestionnaire,
    isSubmitting,
    setEditingFromSummary,
    scrollToQuestion,
    setScrollToQuestion,
  } = useQuestionnaire();

  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!scrollToQuestion || !listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-question="${scrollToQuestion}"]`
    );
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    setScrollToQuestion(null);
  }, [scrollToQuestion, setScrollToQuestion]);

  const displayItems = useMemo(() => {
    const items: { name: string; answer: Answer; displayValue: string }[] = [];
    for (const [name, answer] of Object.entries(answers)) {
      if (!answer.isValid) continue;
      if (!evaluateConditions(answer.question, answers)) continue;
      const displayValue = formatAnswerForDisplay(answer);
      if (displayValue === null) continue;
      items.push({ name, answer, displayValue });
    }
    return items;
  }, [answers]);

  const handleEdit = useCallback(
    (questionName: string) => {
      const pageIndex = visiblePages.findIndex((page) =>
        page.questions.some((q) => q.data.name === questionName)
      );
      if (pageIndex >= 0) {
        setEditingFromSummary(true);
        goToPage(pageIndex);
      }
    },
    [visiblePages, goToPage, setEditingFromSummary]
  );

  return (
    <>
      <ContentCard>
        <QuestionTitle>{t("Review Your Answers")}</QuestionTitle>
        <SummaryList ref={listRef}>
          {displayItems.map(({ name, answer, displayValue }) => (
            <SummaryItem key={name} data-question={name}>
              <SummaryContent>
                <SummaryLabel>
                  {answer.question.data.lead ||
                    answer.question.data.description ||
                    answer.question.data.name}
                </SummaryLabel>
                <SummaryValue>{displayValue}</SummaryValue>
              </SummaryContent>
              <EditLink
                onClick={() => handleEdit(name)}
                aria-label={`${t("Edit")} ${answer.question.data.lead || answer.question.data.description || answer.question.data.name}`}
              >
                {t("Edit")}
              </EditLink>
            </SummaryItem>
          ))}
        </SummaryList>
      </ContentCard>
      <FixedNavBar>
        <NavRow>
          <NavButton
            variant="secondary"
            onClick={() => goToPage(visiblePages.length - 1)}
          >
            {t("Back")}
          </NavButton>
          <NavButton
            variant="primary"
            onClick={submitQuestionnaire}
            disabled={isSubmitting}
          >
            {isSubmitting ? t("Submitting...") : t("Submit")}
          </NavButton>
        </NavRow>
      </FixedNavBar>
    </>
  );
};

export default SummaryPage;
